<script lang="ts" setup>
import { defineProps } from "vue";
import { NewsItem } from "@/types/news";
import { useI18n } from "vue-i18n";
import { useLangStore } from "@/stores/langStore";
import { formatDate } from "@/utils/date";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useRouter } from "vue-router";

const props = defineProps<{
  news: NewsItem;
}>();

const { t } = useI18n();
const langStore = useLangStore();
const router = useRouter();

function openBlog() {
  router.push({
    name: ROUTE_NAMES.BLOG_DETAIL,
    params: { id: props.news.blog_id },
  });
}
</script>

<template>
  <transition name="fade">
    <section :key="news.background_url" class="h-full flex flex-col text-white">
      <img
        :src="news.background_url"
        class="absolute inset-0 w-full h-full object-cover"
        alt="news background"
      />
      <div class="absolute inset-0 bg-gradient"></div>

      <div
        class="absolute bottom-0 flex items-end justify-center px-4 pb-[63px] md:pl-6 md:pb-10 md:pr-[130px] xl:pl-[67px] xl:pb-[4.5rem] xl:pr-40"
      >
        <div class="relative z-10 text-left">
          <div class="flex flex-wrap gap-2 mb-4 md:mb-8 xl:mb-[27px]">
            <div
              v-for="tag in news.tags"
              :key="tag.id"
              class="px-3 py-2 xl:px-4 xl:py-[10px] sf-pro font-normal text-base leading-100p md:text-xl bg-[#FFFFFF33] backdrop-blur-[20px]"
            >
              {{ tag.name }}
            </div>
          </div>
          <p
            class="yeseva-one font-normal text-5xl leading-100p md:text-[64px] xl:text-8xl whitespace-pre-line mb-2 md:mb-4 xl:mb-2"
          >
            {{ news.title }}
          </p>
          <p
            class="sf-pro font-medium text-2xl leading-100p md:text-[32px] xl:text-[40px] whitespace-pre-line mb-4 md:mb-8 xl:mb-10"
          >
            {{ news.subtitle }}
          </p>
          <p
            v-if="news.start_education"
            class="sf-pro font-normal text-sm leading-[21px] md:text-base md:leading-[24px] xl:text-xl xl:leading-[28px] whitespace-pre-line"
          >
            {{ t("carousel.start_education") }}
            <span class="text-gold">{{
              formatDate(news.start_education, langStore.current)
            }}</span>
          </p>
          <p
            class="sf-pro font-normal text-sm leading-[21px] md:text-base md:leading-[24px] xl:text-xl xl:leading-[28px] whitespace-pre-line mb-4 md:mb-8 xl:mb-10"
          >
            {{ news.short_description }}
          </p>

          <button
            class="group relative flex items-center h-[56px] w-[225px] pl-6 pr-1 rounded-full border border-white text-white sf-pro-sm transition-colors duration-300 ease-in-out hover:bg-white"
            @click="openBlog"
          >
            <!-- TEXT -->
            <span
              class="flex-1 text-left transition-colors duration-300 ease-in-out group-hover:text-active"
            >
              {{ t("carousel.show_more") }}
            </span>

            <!-- ARROW ICON -->
            <span
              class="ml-4 w-[46px] h-[46px] flex items-center justify-center rounded-full bg-white transition-colors duration-300 ease-in-out group-hover:bg-orange-gradient"
            >
              <ArrowIcon
                class="text-brown transition-transform duration-300 ease-in-out group-hover:text-white group-hover:rotate-45"
              />
            </span>
          </button>
        </div>
      </div>
    </section>
  </transition>
</template>

<style scoped>
.bg-gradient {
  background: linear-gradient(
    180deg,
    rgba(41, 22, 14, 0) 18.27%,
    rgba(41, 22, 14, 0.75) 70.2%,
    rgba(41, 22, 14, 0.9) 100%
  );
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter-from {
  opacity: 0; /* новая сразу появляется поверх */
}
.fade-leave-to {
  opacity: 0; /* старая исчезает снизу */
}
</style>
